// Job-view top bar — the shared shell (brand · region/env pills · data-as-of +
// Refresh · theme) with this screen's context selectors as children:
// pod → job → scope → run. Filters are standard UI, never per-chart and never
// inside a chart card — every panel below re-renders against the same slice.
import SharedTopBar from './shared/TopBar';
import { runName } from '../lib/format';

export default function TopBar({
  jobs, pod, jobId, scope, mode, runs = [], selectedRun = '',
  onPod, onJob, onScope, onMode, onRun,
  regions = null, region = null, onRegion = null,
  environments = null, environment = null, onEnvironment = null,
  asOf = null, onRefresh = null, refreshing = false,
  nav = null,
}) {
  // Only real pod names — null/undefined must not become a blank <option>.
  const pods = [...new Set(jobs.map((j) => j.pod).filter(Boolean))];
  // Controlled <select value=""> with no matching option renders BLANK in the
  // browser (looks like "all pods removed"). Fall back to the first pod so the
  // dropdown always shows a real selection when jobs exist for this slice.
  const effectivePod = pods.includes(pod) ? pod : (pods[0] || '');
  const jobsForPod = effectivePod
    ? jobs.filter((j) => j.pod === effectivePod)
    : [];
  const effectiveJobId = jobsForPod.some((j) => j.job_id === jobId)
    ? jobId
    : (jobsForPod[0]?.job_id || '');
  const sliceLabel = `${String(region || 'us').toUpperCase()}/${environment || 'all'}`;

  return (
    <SharedTopBar
      regions={regions} region={region} onRegion={onRegion}
      environments={environments} environment={environment} onEnvironment={onEnvironment}
      asOf={asOf} onRefresh={onRefresh} refreshing={refreshing}
      mode={mode} onMode={onMode}
      nav={nav}
    >
      <label className="field">
        <span>Pod</span>
        <select
          value={effectivePod}
          onChange={(e) => onPod(e.target.value)}
          disabled={pods.length === 0}
        >
          {pods.length === 0 ? (
            <option value="">No pods in {sliceLabel}</option>
          ) : (
            pods.map((p) => <option key={p} value={p}>{p}</option>)
          )}
        </select>
      </label>

      <label className="field">
        <span>Job</span>
        <select
          value={effectiveJobId}
          onChange={(e) => onJob(e.target.value)}
          disabled={jobsForPod.length === 0}
        >
          {jobsForPod.length === 0 ? (
            <option value="">No jobs in {sliceLabel}</option>
          ) : (
            jobsForPod.map((j) => <option key={j.job_id} value={j.job_id}>{j.job_id}</option>)
          )}
        </select>
      </label>

      <label className="field">
        <span>Scope</span>
        <select value={scope} onChange={(e) => onScope(e.target.value)}>
          <option value="">all</option>
          <option value="api">api</option>
          <option value="browser">browser</option>
        </select>
      </label>

      {/* One run at a time: signals and details below describe the selected run;
          only the trend chart spans history. Newest first, latest is default. */}
      {runs.length > 0 && (
        <label className="field">
          <span>Run</span>
          <select value={selectedRun} onChange={(e) => onRun(e.target.value)}>
            {runs.map((r, i) => {
              // open_signals comes from /summary trend — 0 means pass-rate history
              // only (common for fixture runs older than the clustered sample).
              const signals = typeof r.open_signals === 'number'
                ? (r.open_signals > 0 ? ` · ${r.open_signals} signals` : ' · no signals')
                : '';
              return (
                <option key={r.run_id} value={r.run_id}>
                  {`${runName(r)}${i === 0 ? ' (latest)' : ''}${signals}`}
                </option>
              );
            })}
          </select>
        </label>
      )}
    </SharedTopBar>
  );
}
